import Button from '@components/Button';
import Loader from '@components/Loader';
import PlanCard from '@components/PlanCard';
import { PLAN_BILL_CYCLE, PLAN_TYPE } from '@utils/constant';
import { Status } from 'hooks/useAxios';
import { useTranslation } from 'next-i18next';
import { useRef } from 'react';
import AliceCarousel from 'react-alice-carousel';

export type PlanData = {
  id: string;
  image: string;
  plan: string;
  medicalCost: number;
  overnightStay: number;
  yearlyPrice: number;
  monthlyPrice: number;
  followUpTreatment: number;
  thirdPartyDamages: number;
  cremationCost: number;
  freeMicroChip: boolean;
  deductiblesAmount: number;
};

const PlanCarousel = ({
  currentStatus,
  handleRetry,
  handleSelect,
  plans,
  billCycle,
  selectedPlanId,
  purchaseType,
}: {
  currentStatus: Status;
  handleRetry: () => void;
  handleSelect: (planData: PlanData) => void;
  plans: PlanData[];
  billCycle: PLAN_BILL_CYCLE;
  selectedPlanId: string;
  purchaseType: PLAN_TYPE;
}) => {
  const { t } = useTranslation('planCard');

  const carousel = useRef<AliceCarousel | null>(null);

  if (currentStatus === Status.Pending || currentStatus === Status.Initial) {
    return <Loader loading={true} />;
  }

  if (currentStatus === Status.Failed) {
    return (
      <section className="p-4">
        <h2>Failed to fetch policies</h2>
        <Button onClick={handleRetry}>Click to retry</Button>
      </section>
    );
  }

  const Plans = plans.map((planData) => (
    <PlanCard
      purchaseType={purchaseType} // PLAN = 0, RENEWAL = 1
      key={planData.id}
      image={planData.image}
      plan={planData.plan}
      billCycle={billCycle}
      yearlyPrice={planData.yearlyPrice}
      monthlyPrice={planData.monthlyPrice}
      medicalCost={planData.medicalCost}
      overnightStay={planData.overnightStay}
      followUpTreatment={planData.followUpTreatment}
      thirdPartyDamages={planData.thirdPartyDamages}
      cremationCost={planData.cremationCost}
      freeMicroChip={planData.freeMicroChip}
      deductiblesAmount={planData.deductiblesAmount}
      onClick={() => handleSelect(planData)}
      disabled={selectedPlanId === planData?.id}
    />
  ));

  return (
    <section className="max-w-[95%] md:max-w-[95%] mx-auto md:flex md:flex-col items-center">
      <h3 className="sr-only">{t('available_plans')}</h3>
      <div className="hidden md:grid grid-cols-3 w-[90vw] gap-4">{Plans}</div>
      <div className="flex flex-row md:hidden">
        <AliceCarousel
          ref={(r) => {
            carousel.current = r;
          }}
          activeIndex={
            selectedPlanId
              ? plans.findIndex((item) => item.id === selectedPlanId)
              : 0
          }
          infinite
          mouseTracking
          paddingLeft={16}
          paddingRight={16}
          items={Plans.map((planComponent, index) => (
            <div key={`plan-${index}`} className="mx-1">
              {planComponent}
            </div>
          ))}
          responsive={{
            0: { items: 1 },
            800: { items: 2 },
            1024: { items: 3 },
          }}
        />
      </div>
    </section>
  );
};

export default PlanCarousel;
